import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    useTheme,
    Alert,
    Snackbar,
    Tooltip,
    Switch,
    FormControlLabel,
    Chip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { CollectionPoint, CollectionPointDataElementMap, collectionPointsService } from '../../services/collectionpoints-service';
import { DataElement, dataElementsService } from '../../services/dataelements-service';
import { config } from '../../config/config';

interface DataElementsTabProps {
    collectionPoint: CollectionPoint | null;
    isPublished: boolean;
    onDataElementsChanged?: () => void;
    onHasChanges?: (hasChanges: boolean) => void;
}

const DataElementsTab: React.FC<DataElementsTabProps> = ({
    collectionPoint,
    isPublished,
    onDataElementsChanged,
    onHasChanges
}) => {
    const theme = useTheme();

    // Data elements state
    const [dataElementMappings, setDataElementMappings] = useState<CollectionPointDataElementMap[]>([]);
    const [originalMappings, setOriginalMappings] = useState<CollectionPointDataElementMap[]>([]);
    const [loadingDataElements, setLoadingDataElements] = useState(false);
    const [loadingAvailableElements, setLoadingAvailableElements] = useState(false);
    const [availableDataElements, setAvailableDataElements] = useState<DataElement[]>([]);
    const [hasDataElementsChanges, setHasDataElementsChanges] = useState(false);

    // Error and success states
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [mappingElementId, setMappingElementId] = useState<number | null>(null);
    const [unmappingElementId, setUnmappingElementId] = useState<number | null>(null);

    // State for tracking "isRequired" values for each data element
    const [requiredStates, setRequiredStates] = useState<Record<number, boolean>>({});
    
    // State for filtered available data elements (excluding already mapped ones)
    const [filteredAvailableDataElements, setFilteredAvailableDataElements] = useState<DataElement[]>([]);

    // Check if there are unsaved changes
    const checkForChanges = useCallback(() => {
        if (!originalMappings.length && !dataElementMappings.length) {
            return false;
        }

        if (originalMappings.length !== dataElementMappings.length) {
            return true;
        }

        // Compare each mapping
        const originalIds = originalMappings.map(m => m.DataElementId).sort();
        const currentIds = dataElementMappings.map(m => m.DataElementId).sort();

        return JSON.stringify(originalIds) !== JSON.stringify(currentIds);
    }, [originalMappings, dataElementMappings]);

    // Update change tracking
    useEffect(() => {
        const hasChanges = checkForChanges();
        setHasDataElementsChanges(hasChanges);
        if (onHasChanges) {
            onHasChanges(hasChanges);
        }
    }, [checkForChanges, onHasChanges]);

    // Load mapped data elements
    const loadMappedDataElements = async () => {
        if (!collectionPoint?.CollectionPointId) return;
        
        try {
            setLoadingDataElements(true);
            setError(null);
            const mappings = await collectionPointsService.getMappedDataElements(collectionPoint.CollectionPointId);
            setDataElementMappings(mappings);
            setOriginalMappings(mappings); // Store original state for change tracking
        } catch (error) {
            console.error('Error loading mapped data elements:', error);
            setError('Failed to load mapped data elements. Please try again.');
        } finally {
            setLoadingDataElements(false);
        }
    };

    // Handle required toggle for data elements
    const handleRequiredToggle = (elementId: number, isRequired: boolean) => {
        setRequiredStates(prev => ({
            ...prev,
            [elementId]: isRequired
        }));
    };

    // Load available data elements
    const loadAvailableDataElements = async () => {
        if (!collectionPoint?.CollectionPointId || isPublished) return;
        
        try {
            setLoadingAvailableElements(true);
            setError(null);
            const response = await dataElementsService.getDataElements(
                { isPublished: true }, // Only fetch published data elements
                { page: 1, pageSize: 1000 }, // Large page size to get all published elements
                'A-Z'
            );
            // Don't filter here - let the useEffect handle filtering
            setAvailableDataElements(response.elements);
        } catch (error) {
            console.error('Error loading available data elements:', error);
            setError('Failed to load available data elements. Please try again.');
        } finally {
            setLoadingAvailableElements(false);
        }
    };

    // Handle mapping a data element
    const handleMapDataElement = async (dataElementId: number) => {
        if (!collectionPoint?.CollectionPointId) return;
        
        try {
            setMappingElementId(dataElementId);
            setError(null);
            
            // Get the isRequired value for this data element (default to false if not set)
            const isRequired = requiredStates[dataElementId] || false;
            
            const newMapping = await collectionPointsService.mapDataElement(
                collectionPoint.CollectionPointId, 
                { dataElementId, isRequired }
            );
            setSuccessMessage('Data element mapped successfully!');
            // Refresh only the data elements data, not the entire collection point
            await loadMappedDataElements();
        } catch (error) {
            console.error('Error mapping data element:', error);
            
            // Provide more specific error messages
            let errorMsg = 'Failed to map data element. Please try again.';
            if (error instanceof Error) {
                if (error.message.includes('ValidationError')) {
                    errorMsg = 'Validation error: Please check your input values.';
                } else if (error.message.includes('NotFoundError')) {
                    errorMsg = 'Collection point not found or does not belong to your client.';
                } else if (error.message.includes('already mapped')) {
                    errorMsg = 'This data element is already mapped to this collection point.';
                } else if (error.message.includes('published')) {
                    errorMsg = 'Cannot modify a published collection point.';
                } else {
                    errorMsg = error.message;
                }
            }
            
            setError(errorMsg);
        } finally {
            setMappingElementId(null);
        }
    };

    // Handle unmapping a data element
    const handleUnmapDataElement = async (dataElementId: number) => {
        if (!collectionPoint?.CollectionPointId) return;
        
        // Find the data element mapping to get the element name for the confirmation dialog
        const dataElementMapping = dataElementMappings.find(mapping => mapping.DataElementId === dataElementId);
        if (!dataElementMapping) {
            setError('Data element mapping not found.');
            return;
        }
        
        // Show browser confirmation dialog
        const isConfirmed = window.confirm(`Are you sure you want to remove the data element mapping for "${dataElementMapping.elementName}"? This action cannot be undone.`);
        
        if (!isConfirmed) {
            return; // User cancelled the operation
        }
        
        try {
            setUnmappingElementId(dataElementId);
            setError(null);
            await collectionPointsService.unmapDataElement(collectionPoint.CollectionPointId, dataElementId);
            setSuccessMessage('Data element unmapped successfully!');
            // Refresh only the data elements data, not the entire collection point
            await loadMappedDataElements();
        } catch (error) {
            console.error('Error unmapping data element:', error);
            
            // Provide more specific error messages
            let errorMsg = 'Failed to unmap data element. Please try again.';
            if (error instanceof Error) {
                if (error.message.includes('NotFoundError')) {
                    errorMsg = 'Data element mapping not found or does not belong to this collection point.';
                } else if (error.message.includes('published')) {
                    errorMsg = 'Cannot modify a published collection point.';
                } else {
                    errorMsg = error.message;
                }
            }
            
            setError(errorMsg);
        } finally {
            setUnmappingElementId(null);
        }
    };

    // Reset changes (called when parent saves or cancels)
    const resetChanges = useCallback(() => {
        setDataElementMappings([...originalMappings]);
        setHasDataElementsChanges(false);
        setError(null);
        setSuccessMessage(null);
    }, [originalMappings]);

    // Expose reset function to parent (if needed)
    useEffect(() => {
        // This could be used by parent to reset changes
        // For now, we handle it internally
    }, []);

    // Load data when component mounts or collection point changes
    useEffect(() => {
        if (collectionPoint?.CollectionPointId) {
            loadMappedDataElements();
        }
    }, [collectionPoint?.CollectionPointId]);

    // Load available data elements when mapped elements change
    useEffect(() => {
        loadAvailableDataElements();
    }, [dataElementMappings, collectionPoint?.CollectionPointId, isPublished]);

    // Filter available data elements to exclude already mapped ones
    useEffect(() => {
        if (availableDataElements.length > 0) {
            const mappedDataElementIds = dataElementMappings.map(mde => mde.DataElementId);
            const available = availableDataElements.filter(element => 
                element.elementId && !mappedDataElementIds.includes(element.elementId)
            );
            setFilteredAvailableDataElements(available);
        } else {
            setFilteredAvailableDataElements([]);
        }
    }, [availableDataElements, dataElementMappings]);

    // Reset changes when collection point changes
    useEffect(() => {
        setHasDataElementsChanges(false);
        setError(null);
        setSuccessMessage(null);
        setOriginalMappings([]);
        setDataElementMappings([]);
        setFilteredAvailableDataElements([]);
        setRequiredStates({}); // Reset required states when collection point changes
    }, [collectionPoint?.CollectionPointId]);

    return (
        <Box sx={{ p: 3 }}>

            {/* Success Snackbar */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
                onClose={() => setSuccessMessage(null)}
                message={successMessage}
            />
            
            {/* Error Snackbar */}
            <Snackbar
                key={error} // Force re-render when message changes
                open={!!error}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
                onClose={() => setError(null)}
                message={error}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        bgcolor: theme.palette.error.main,
                    },
                }}
            />

            {/* Change indicator */}
            {hasDataElementsChanges && (
                <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    action={
                        <Button 
                            color="inherit" 
                            size="small" 
                            onClick={resetChanges}
                        >
                            Reset
                        </Button>
                    }
                >
                    You have unsaved changes to data elements mapping.
                </Alert>
            )}

            {loadingDataElements ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography color="text.primary">Loading data elements...</Typography>
                </Box>
            ) : (
                <>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        Mapped Data Elements
                    </Typography>
                    {dataElementMappings.length === 0 ? (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No data elements mapped to this collection point.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                            {dataElementMappings.map((mapping) => (
                                <Box key={mapping.DataElementId} sx={{
                                    minWidth: 320,
                                    maxWidth: 400,
                                    flex: '1 1 320px',
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: theme.shape.borderRadius,
                                    boxShadow: theme.shadows[2],
                                    p: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    position: 'relative',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" color="text.primary" sx={{ flex: 1 }}>{mapping.elementName}</Typography>
                                        {!isPublished && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                disabled={unmappingElementId === mapping.DataElementId}
                                                onClick={() => handleUnmapDataElement(mapping.DataElementId)}
                                                sx={{
                                                    borderColor: theme.palette.error.main,
                                                    color: theme.palette.error.main,
                                                    '&:hover': {
                                                        borderColor: theme.palette.error.dark,
                                                        bgcolor: theme.palette.error.light,
                                                    },
                                                    '&:disabled': {
                                                        borderColor: theme.palette.action.disabledBackground,
                                                        color: theme.palette.action.disabled,
                                                    },
                                                }}
                                            >
                                                {unmappingElementId === mapping.DataElementId ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    'Remove'
                                                )}
                                            </Button>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {mapping.elementDescription}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Type: {mapping.dataType}
                                        </Typography>
                                        <Chip
                                            icon={mapping.IsRequired ? <StarIcon /> : <StarBorderIcon />}
                                            label={mapping.IsRequired ? 'Required' : 'Optional'}
                                            size="small"
                                            variant={mapping.IsRequired ? 'filled' : 'outlined'}
                                            sx={{
                                                fontSize: '0.7rem',
                                                height: 24,
                                                '& .MuiChip-icon': {
                                                    fontSize: '1rem',
                                                },
                                                ...(mapping.IsRequired ? {
                                                    bgcolor: theme.palette.error.main,
                                                    color: theme.palette.error.contrastText,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.error.dark,
                                                    },
                                                } : {
                                                    bgcolor: 'transparent',
                                                    color: theme.palette.text.secondary,
                                                    borderColor: theme.palette.divider,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.action.hover,
                                                    },
                                                }),
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            Order: {mapping.DisplayOrder}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {!isPublished && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                                    Available Data Elements
                                </Typography>
                                {loadingAvailableElements && (
                                    <CircularProgress size={16} />
                                )}
                            </Box>
                            {filteredAvailableDataElements.length === 0 && !loadingAvailableElements ? (
                                <Typography color="text.secondary" sx={{ py: 2 }}>
                                    {availableDataElements.length === 0 ? 'No available data elements to map.' : 'All available data elements are already mapped.'}
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {filteredAvailableDataElements.map((element) => (
                                        <Box key={element.elementId} sx={{
                                            minWidth: 320,
                                            maxWidth: 400,
                                            flex: '1 1 320px',
                                            bgcolor: theme.palette.background.paper,
                                            borderRadius: theme.shape.borderRadius,
                                            boxShadow: theme.shadows[2],
                                            p: 3,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1.5,
                                            border: `1px solid ${theme.palette.divider}`,
                                            position: 'relative',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="h6" color="text.primary" sx={{ flex: 1 }}>{element.elementName}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={requiredStates[element.elementId || 0] || false}
                                                                onChange={(e) => element.elementId && handleRequiredToggle(element.elementId, e.target.checked)}
                                                                size="small"
                                                                disabled={mappingElementId === element.elementId}
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase': {
                                                                        color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[300],
                                                                    },
                                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                                        color: theme.palette.primary.main,
                                                                    },
                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                        backgroundColor: theme.palette.primary.main,
                                                                    },
                                                                    '& .MuiSwitch-track': {
                                                                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                                                                    },
                                                                    '& .MuiSwitch-switchBase.Mui-disabled': {
                                                                        color: theme.palette.action.disabled,
                                                                    },
                                                                    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                                                                        backgroundColor: theme.palette.action.disabledBackground,
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography 
                                                                variant="caption" 
                                                                sx={{ 
                                                                    fontSize: '0.75rem',
                                                                    color: theme.palette.text.secondary
                                                                }}
                                                            >
                                                                Required
                                                            </Typography>
                                                        }
                                                        sx={{ mr: 0 }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        disabled={mappingElementId === element.elementId}
                                                        onClick={() => element.elementId && handleMapDataElement(element.elementId)}
                                                        sx={{
                                                            bgcolor: theme.palette.primary.main,
                                                            color: theme.palette.primary.contrastText,
                                                            '&:hover': {
                                                                bgcolor: theme.palette.primary.dark,
                                                            },
                                                            '&:disabled': {
                                                                bgcolor: theme.palette.action.disabledBackground,
                                                                color: theme.palette.action.disabled,
                                                            },
                                                        }}
                                                    >
                                                        {mappingElementId === element.elementId ? (
                                                            <CircularProgress size={16} color="inherit" />
                                                        ) : (
                                                            'Add'
                                                        )}
                                                    </Button>
                                                </Box>
                                            </Box>
                                            
                                            {/* Type Name below the name */}
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                {element.type?.typeName || 'Unknown Type'}
                                            </Typography>
                                            
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {element.description}
                                            </Typography>
                                            
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Type: {element.type?.typeName || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Code: {element.elementCode}
                                                </Typography>
                                                {element.options && element.options.length > 0 && (
                                                    <Tooltip
                                                        title={
                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                                    Options ({element.options.length}):
                                                                </Typography>
                                                                {element.options.map((option, index) => (
                                                                    <Typography key={option.optionId || index} variant="caption" sx={{ display: 'block' }}>
                                                                        • {option.optionLabel || option.optionValue}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        }
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                                                            Options: {element.options.length}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                                <Typography variant="caption" color="text.secondary">
                                                    Identifier: {element.isIdentifier ? 'Yes' : 'No'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Personal Data: {element.isPersonalData ? 'Yes' : 'No'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default DataElementsTab; 